import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

/* ==========================================================
   CONFIG  (baaki resort pages wali same API_BASE)
========================================================== */
const API_BASE = "https://bmtvendorvendorfinal.onrender.com";
const RESORT_API = `${API_BASE}/api/resorts`;
const ROOM_API = `${API_BASE}/api/resort-rooms`;
const UNIT_API = `${API_BASE}/api/resort-room-units`;
const INVENTORY_API = `${API_BASE}/api/resort-inventory`;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

/* SOLD_OUT backend khud lagata hai, isliye form me sirf ye 3 */
const FORM_STATUSES = [
  ["AVAILABLE", "Open for booking"],
  ["BLOCKED", "Blocked (no rooms bookable)"],
  ["CLOSED", "Closed (no rooms bookable)"],
];

const STATUS_STYLE = {
  AVAILABLE: { label: "Available", color: "#15803d", bg: "#f0fdf4" },
  SOLD_OUT: { label: "Sold out", color: "#b91c1c", bg: "#fef2f2" },
  BLOCKED: { label: "Blocked", color: "#6d28d9", bg: "#f5f3ff" },
  CLOSED: { label: "Closed", color: "#4b5563", bg: "#f3f4f6" },
};

const EMPTY_FORM = {
  roomCategoryId: "",
  from: "",
  to: "",
  blockedRooms: 0,
  maintenanceRooms: 0,
  status: "AVAILABLE",
  notes: "",
  days: ALL_DAYS,
};

/* ==========================================================
   HELPERS
========================================================== */
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("vendorToken") || ""}` });

async function api(url, options = {}) {
  let res;
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        ...authHeaders(),
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...options.headers,
      },
    });
  } catch {
    throw new Error("Cannot reach the server. Check that the backend is running.");
  }

  let data = {};
  try { data = await res.json(); } catch { data = {}; }

  if (res.status === 401 && data.code === "TOKEN_EXPIRED") {
    localStorage.removeItem("vendorToken");
    localStorage.removeItem("vendorUser");
    window.location.href = "/vendor-login";
    throw new Error("Session expired. Please log in again.");
  }

  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

const num = (v, fallback = 0) => (v === "" || v === null || v === undefined || Number.isNaN(Number(v)) ? fallback : Number(v));
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`; // m = 0..11
const todayYmd = () => {
  const t = new Date();
  return ymd(t.getFullYear(), t.getMonth(), t.getDate());
};
const fmtDate = (iso) =>
  new Date(`${String(iso).slice(0, 10)}T00:00:00.000Z`).toLocaleDateString("en-IN", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC",
  });

/* from..to (inclusive), weekday filter (0 = Sun), UTC-safe */
function dateRange(from, to, days) {
  const out = [];
  const cur = new Date(`${from}T00:00:00.000Z`);
  const end = new Date(`${to}T00:00:00.000Z`);
  while (cur <= end && out.length < 400) {
    if (days.includes(cur.getUTCDay())) out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

/* Backend jaisa hi count: active units = isActive != false AND status != INACTIVE.
   Existing route GET /resort-room-units/resort/:resortId use hota hai (naya route nahi chahiye). */
async function loadRoomCounts(resortId, categoryId) {
  const data = await api(`${UNIT_API}/resort/${resortId}`);
  const all = Array.isArray(data.roomUnits) ? data.roomUnits : [];
  const active = all.filter((u) => {
    const cid = u.roomCategory?._id || u.roomCategory;
    return String(cid) === String(categoryId) && u.isActive !== false && u.status !== "INACTIVE";
  });
  return {
    total: active.length,
    blocked: active.filter((u) => u.status === "BLOCKED").length,
    maintenance: active.filter((u) => u.status === "MAINTENANCE").length,
  };
}

/* ==========================================================
   SMALL UI PIECES
========================================================== */
function Input({ label, value, onChange, type = "text", placeholder, required, hint, ...rest }) {
  return (
    <div className="bmt-field-group">
      <label>{label}{required && " *"}</label>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} {...rest} />
      {hint && <small style={{ color: "#6b7280" }}>{hint}</small>}
    </div>
  );
}

function Notice({ kind, children }) {
  if (!children) return null;
  const bad = kind === "error";
  return (
    <div
      role={bad ? "alert" : "status"}
      style={{
        padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: 14,
        background: bad ? "#fef2f2" : "#f0fdf4", color: bad ? "#991b1b" : "#166534",
        border: `1px solid ${bad ? "#fecaca" : "#bbf7d0"}`,
      }}
    >
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.AVAILABLE;
  return (
    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, color: s.color, background: s.bg, border: `1px solid ${s.color}33` }}>
      {s.label}
    </span>
  );
}

/* ==========================================================
   MAIN COMPONENT
========================================================== */
export default function ResortInventory() {
  /* ----- selection ----- */
  const [resorts, setResorts] = useState([]);
  const [resortId, setResortId] = useState(() => sessionStorage.getItem("bmtResortInventoryResort") || "");
  const [loadingResorts, setLoadingResorts] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("ALL");

  /* ----- list / calendar data ----- */
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [displayMode, setDisplayMode] = useState("list"); // list | calendar
  const [calTotal, setCalTotal] = useState(0);
  const [expandedId, setExpandedId] = useState("");

  /* ----- form ----- */
  const [view, setView] = useState("list"); // list | form
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formCounts, setFormCounts] = useState(null); // { total, blocked, maintenance }
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const resortName = resorts.find((r) => r._id === resortId)?.name || "";
  const catIdOf = (rec) => rec.roomCategory?._id || rec.roomCategory;
  const catNameOf = (rec) => rec.roomCategory?.name || rec.roomCategory?.title || categories.find((c) => c._id === catIdOf(rec))?.name || "—";

  /* ---------- LOAD RESORTS ---------- */
  useEffect(() => {
    (async () => {
      try {
        const data = await api(`${RESORT_API}/my-resorts`);
        const list = Array.isArray(data.resorts) ? data.resorts : Array.isArray(data.data) ? data.data : [];
        setResorts(list);
        setResortId((cur) => (list.some((r) => r._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingResorts(false);
      }
    })();
  }, []);

  /* ---------- LOAD CATEGORIES ---------- */
  useEffect(() => {
    setCategoryFilter("ALL");
    if (!resortId) { setCategories([]); return; }
    sessionStorage.setItem("bmtResortInventoryResort", resortId);
    let live = true;
    (async () => {
      try {
        const data = await api(`${ROOM_API}/resort/${resortId}`);
        if (live) setCategories(Array.isArray(data.rooms) ? data.rooms : []);
      } catch (e) {
        if (live) { setCategories([]); setError(e.message); }
      }
    })();
    return () => { live = false; };
  }, [resortId]);

  /* ---------- LOAD INVENTORY OF VISIBLE MONTH ---------- */
  const loadRecords = useCallback(async () => {
    if (!resortId) { setRecords([]); return; }
    setLoadingRecords(true);
    try {
      const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      let qs = `startDate=${ymd(year, month, 1)}&endDate=${ymd(year, month, last)}`;
      if (categoryFilter !== "ALL") qs += `&roomCategoryId=${categoryFilter}`;
      const data = await api(`${INVENTORY_API}/resort/${resortId}?${qs}`);
      setRecords(Array.isArray(data.inventory) ? data.inventory : []);
    } catch (e) {
      setRecords([]);
      setError(e.message);
    } finally {
      setLoadingRecords(false);
    }
  }, [resortId, categoryFilter, year, month]);

  useEffect(() => { setExpandedId(""); loadRecords(); }, [loadRecords]);

  /* calendar: selected category ke active rooms */
  useEffect(() => {
    if (categoryFilter === "ALL" || !resortId) { setCalTotal(0); return; }
    let live = true;
    loadRoomCounts(resortId, categoryFilter)
      .then((c) => live && setCalTotal(c.total))
      .catch(() => live && setCalTotal(0));
    return () => { live = false; };
  }, [resortId, categoryFilter]);

  /* form: selected category ke counts */
  useEffect(() => {
    if (view !== "form" || !form.roomCategoryId || !resortId) { setFormCounts(null); return; }
    let live = true;
    setFormCounts(null);
    loadRoomCounts(resortId, form.roomCategoryId)
      .then((c) => live && setFormCounts(c))
      .catch((e) => { if (live) { setFormCounts({ total: 0, blocked: 0, maintenance: 0 }); setError(e.message); } });
    return () => { live = false; };
  }, [view, resortId, form.roomCategoryId]);

  /* ---------- DERIVED ---------- */
  const shown = useMemo(
    () => (statusFilter === "ALL" ? records : records.filter((r) => r.status === statusFilter)),
    [records, statusFilter]
  );

  const counts = useMemo(() => {
    const c = {};
    Object.keys(STATUS_STYLE).forEach((k) => { c[k] = records.filter((r) => r.status === k).length; });
    return c;
  }, [records]);

  const recordByDate = useMemo(() => {
    const m = {};
    if (categoryFilter !== "ALL") records.forEach((r) => { m[String(r.date).slice(0, 10)] = r; });
    return m;
  }, [records, categoryFilter]);

  const calendarCells = useMemo(() => {
    const firstDow = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const total = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push(null);
    for (let d = 1; d <= total; d += 1) cells.push({ day: d, date: ymd(year, month, d) });
    return cells;
  }, [year, month]);

  const targetDates = useMemo(
    () => (editing ? [form.from] : form.from && form.to && form.to >= form.from ? dateRange(form.from, form.to, form.days) : []),
    [editing, form.from, form.to, form.days]
  );

  const closing = form.status === "CLOSED" || form.status === "BLOCKED";
  const total = formCounts?.total ?? 0;
  // Backend: Room Units me jo BLOCKED/MAINTENANCE hain, wo minimum hote hain
  const effBlocked = Math.max(formCounts?.blocked ?? 0, num(form.blockedRooms));
  const effMaint = Math.max(formCounts?.maintenance ?? 0, num(form.maintenanceRooms));
  const bookedNow = editing ? editing.bookedRooms || 0 : 0;
  const roomsLeft = Math.max(total - bookedNow - effBlocked - effMaint, 0);

  const shiftMonth = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  /* ==========================================================
     OPEN FORM
  ========================================================== */
  const openAdd = (date = "") => {
    setForm({
      ...EMPTY_FORM,
      roomCategoryId: categoryFilter !== "ALL" ? categoryFilter : categories[0]?._id || "",
      from: date,
      to: date,
    });
    setEditing(null);
    setError("");
    setSuccess("");
    setView("form");
  };

  const openEdit = (rec) => {
    const d = String(rec.date).slice(0, 10);
    setForm({
      roomCategoryId: catIdOf(rec),
      from: d,
      to: d,
      blockedRooms: rec.blockedRooms ?? 0,
      maintenanceRooms: rec.maintenanceRooms ?? 0,
      status: rec.status === "BLOCKED" || rec.status === "CLOSED" ? rec.status : "AVAILABLE",
      notes: rec.notes || "",
      days: ALL_DAYS,
    });
    setEditing(rec);
    setError("");
    setSuccess("");
    setView("form");
  };

  const closeForm = () => {
    setView("list");
    setError("");
  };

  const toggleDay = (d) =>
    setForm((p) => ({ ...p, days: p.days.includes(d) ? p.days.filter((x) => x !== d) : [...p.days, d] }));

  /* ==========================================================
     SAVE
     - Edit: PUT /:inventoryId
     - Add : jis date ka record hai uspe PUT, baaki dates par POST
             (backend POST existing par 409 deta hai)
  ========================================================== */
  const save = async () => {
    setError("");
    setSuccess("");

    if (!form.roomCategoryId) return setError("Choose a room category.");
    if (!formCounts || formCounts.total === 0) return setError("This category has no active room units. Add room units first.");
    if (!form.from) return setError("Choose a date.");
    if (!editing && (!form.to || form.to < form.from)) return setError("End date cannot be before start date.");
    if (![form.blockedRooms, form.maintenanceRooms].every((v) => Number.isInteger(num(v, -1)) && num(v, -1) >= 0)) {
      return setError("Blocked and maintenance rooms must be whole numbers, 0 or more.");
    }
    if (effBlocked + effMaint + bookedNow > total) {
      return setError(
        editing
          ? `Booked (${bookedNow}) + blocked (${effBlocked}) + maintenance (${effMaint}) rooms cannot be more than total rooms (${total}).`
          : `Blocked (${effBlocked}) + maintenance (${effMaint}) rooms cannot be more than total rooms (${total}).`
      );
    }
    if (targetDates.length === 0) return setError("No dates match the selected days of the week.");
    if (targetDates.length > 366) return setError("You can update up to 366 dates at a time.");

    setSaving(true);

    // Effective values bhejte hain taaki frontend aur backend ke numbers same rahein
    const payload = {
      blockedRooms: effBlocked,
      maintenanceRooms: effMaint,
      status: form.status,
      notes: form.notes.trim(),
    };

    try {
      /* ---- single edit ---- */
      if (editing) {
        await api(`${INVENTORY_API}/${editing._id}`, { method: "PUT", body: JSON.stringify(payload) });
        await loadRecords();
        setView("list");
        setSuccess("Availability updated.");
        return;
      }

      /* ---- add (one or many dates) ---- */
      const existingRes = await api(
        `${INVENTORY_API}/resort/${resortId}?roomCategoryId=${form.roomCategoryId}&startDate=${form.from}&endDate=${form.to}`
      );
      const existing = {};
      (existingRes.inventory || []).forEach((r) => { existing[String(r.date).slice(0, 10)] = r._id; });

      let created = 0;
      let updated = 0;
      const failures = {}; // message -> dates[]

      for (let i = 0; i < targetDates.length; i += 10) {
        const batch = targetDates.slice(i, i + 10);
        const results = await Promise.allSettled(
          batch.map((date) =>
            existing[date]
              ? api(`${INVENTORY_API}/${existing[date]}`, { method: "PUT", body: JSON.stringify(payload) })
              : api(INVENTORY_API, {
                  method: "POST",
                  body: JSON.stringify({ resortId, roomCategoryId: form.roomCategoryId, date, ...payload }),
                })
          )
        );
        results.forEach((r, idx) => {
          if (r.status === "fulfilled") {
            if (existing[batch[idx]]) updated += 1;
            else created += 1;
          } else {
            const msg = r.reason?.message || "Failed";
            (failures[msg] = failures[msg] || []).push(batch[idx]);
          }
        });
      }

      const failedCount = Object.values(failures).reduce((n, a) => n + a.length, 0);
      const failText = failedCount
        ? `${failedCount} dates were not saved. ${Object.entries(failures)
            .map(([msg, dates]) => `${msg} (${dates.slice(0, 3).join(", ")}${dates.length > 3 ? ` +${dates.length - 3} more` : ""})`)
            .join(" • ")}`
        : "";

      if (created + updated === 0) {
        setError(failText || "Nothing was saved.");
        return;
      }

      const [y, m] = targetDates[0].split("-").map(Number);
      setYear(y);
      setMonth(m - 1);
      if (categoryFilter !== "ALL" && categoryFilter !== form.roomCategoryId) setCategoryFilter(form.roomCategoryId);
      setView("list");
      const parts = [];
      if (created) parts.push(`${created} date${created > 1 ? "s" : ""} added`);
      if (updated) parts.push(`${updated} date${updated > 1 ? "s" : ""} updated`);
      setSuccess(`${parts.join(", ")}.`);
      if (failText) setError(failText);
      loadRecords();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  /* ==========================================================
     DELETE
  ========================================================== */
  const remove = async (rec, fromForm = false) => {
    if ((rec.bookedRooms || 0) > 0) {
      setError("This date has booked rooms, so its record cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete the availability record for ${fmtDate(rec.date)} (${catNameOf(rec)})? After this, all rooms are free on that date.`)) return;
    setError("");
    setSuccess("");
    try {
      await api(`${INVENTORY_API}/${rec._id}`, { method: "DELETE" });
      setRecords((list) => list.filter((r) => r._id !== rec._id));
      if (fromForm) setView("list");
      setSuccess("Availability record deleted.");
    } catch (e) {
      setError(e.message);
    }
  };

  /* ==========================================================
     HEADER
  ========================================================== */
  const header = (
    <div className="bmt-section-intro">
      <div>
        <h2>{view === "form" ? (editing ? "Edit Availability" : "Add Availability") : "Availability"}</h2>
        <p>
          {view === "form"
            ? `${resortName}${editing ? ` • ${fmtDate(editing.date)}` : ""}`
            : "See how many rooms are free on each date, and block rooms, mark maintenance or close dates. Bookings reduce availability automatically."}
        </p>
      </div>
      {view === "form" ? (
        <button className="bmt-secondary-btn" onClick={closeForm}>Back to list</button>
      ) : (
        <button className="bmt-primary-btn" onClick={() => openAdd()} disabled={!resortId || categories.length === 0}>
          + Add Availability
        </button>
      )}
    </div>
  );

  if (!loadingResorts && resorts.length === 0) {
    return (
      <div className="bmt-premium-page">
        {header}
        <Notice kind="error">{error}</Notice>
        <section className="bmt-form-card">
          <div className="bmt-empty-state">
            <h3>Add a resort first</h3>
            <p>Availability is managed per room category. Add your resort, room categories and room units from the menu, then come back here.</p>
          </div>
        </section>
      </div>
    );
  }

  /* ==========================================================
     FORM VIEW
  ========================================================== */
  if (view === "form") {
    return (
      <div className="bmt-premium-page">
        {header}
        <Notice kind="error">{error}</Notice>

        <section className="bmt-form-card">
          <div className="bmt-form-card-head"><h3>Dates and room category</h3></div>
          <div className="bmt-form-grid">
            <div className="bmt-field-group">
              <label>Room category *</label>
              <select value={form.roomCategoryId} onChange={(e) => setField("roomCategoryId", e.target.value)} disabled={!!editing}>
                <option value="" disabled>Select category</option>
                {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
              {formCounts && (
                <small style={{ color: formCounts.total === 0 ? "#b91c1c" : "#6b7280" }}>
                  {formCounts.total === 0
                    ? "No active room units in this category. Add room units first."
                    : `${formCounts.total} active room${formCounts.total === 1 ? "" : "s"} in this category`}
                </small>
              )}
            </div>

            {editing ? (
              <div className="bmt-field-group">
                <label>Date</label>
                <div style={{ padding: "10px 0", fontWeight: 600 }}>{fmtDate(editing.date)}</div>
              </div>
            ) : (
              <>
                <Input label="From date" required type="date" value={form.from} onChange={(v) => setForm((p) => ({ ...p, from: v, to: p.to && p.to >= v ? p.to : v }))} />
                <Input label="To date" required type="date" min={form.from || undefined} value={form.to} onChange={(v) => setField("to", v)} />
              </>
            )}
          </div>

          {!editing && (
            <div className="bmt-field-group">
              <label>Apply on these days {targetDates.length > 0 && <span style={{ color: "#6b7280", fontWeight: 400 }}>({targetDates.length} date{targetDates.length > 1 ? "s" : ""} selected)</span>}</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {WEEKDAYS.map((d, i) => (
                  <label key={d} className={`bmt-check-card ${form.days.includes(i) ? "selected" : ""}`} style={{ padding: "6px 12px" }}>
                    <input type="checkbox" checked={form.days.includes(i)} onChange={() => toggleDay(i)} />
                    <span>{d}</span>
                  </label>
                ))}
                <button type="button" className="bmt-secondary-btn" onClick={() => setField("days", [5, 6])}>Fri-Sat only</button>
                <button type="button" className="bmt-secondary-btn" onClick={() => setField("days", [1, 2, 3, 4])}>Mon-Thu only</button>
                <button type="button" className="bmt-secondary-btn" onClick={() => setField("days", ALL_DAYS)}>All days</button>
              </div>
              <small style={{ color: "#6b7280" }}>Dates that already have a record will be updated. Booked rooms are never changed.</small>
            </div>
          )}
        </section>

        <section className="bmt-form-card">
          <div className="bmt-form-card-head"><h3>Availability</h3></div>
          <div className="bmt-form-grid">
            <div className="bmt-field-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => setField("status", e.target.value)}>
                {FORM_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
            {!closing && (
              <>
                <Input
                  label="Blocked rooms" type="number" min={formCounts?.blocked || 0} max={total || undefined}
                  value={form.blockedRooms} onChange={(v) => setField("blockedRooms", v)}
                  hint="Rooms you keep aside (owner use, offline sale)"
                />
                <Input
                  label="Rooms under maintenance" type="number" min={formCounts?.maintenance || 0} max={total || undefined}
                  value={form.maintenanceRooms} onChange={(v) => setField("maintenanceRooms", v)}
                  hint="Rooms not usable on these dates"
                />
              </>
            )}
          </div>

          {!closing && formCounts && formCounts.total > 0 && (
            <>
              {(formCounts.blocked > 0 || formCounts.maintenance > 0) && (
                <p style={{ margin: "4px 0 6px", fontSize: 13, color: "#b45309" }}>
                  In Room Units, {formCounts.blocked} room{formCounts.blocked === 1 ? " is" : "s are"} marked Blocked and {formCounts.maintenance} Maintenance.
                  These are always counted, so blocked rooms here cannot go below {formCounts.blocked} and maintenance rooms below {formCounts.maintenance}.
                </p>
              )}
              <p style={{ margin: "4px 0 12px", fontSize: 14 }}>
                Rooms left for guests: <b>{roomsLeft}</b>
                <span style={{ color: "#6b7280" }}> of {total}{editing ? ` (${bookedNow} booked, ${effBlocked} blocked, ${effMaint} maintenance)` : " (before bookings)"}</span>
              </p>
            </>
          )}
          {closing && (
            <Notice kind="success">
              All rooms will be unavailable on the selected date{targetDates.length > 1 ? "s" : ""}. Existing bookings are not cancelled.
            </Notice>
          )}

          <div className="bmt-field-group">
            <label>Notes</label>
            <input value={form.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="e.g. Wedding booking by owner, AC servicing" />
          </div>
        </section>

        <div className="bmt-form-actions">
          {editing && (
            <button
              className="bmt-secondary-btn"
              style={{ marginRight: "auto" }}
              disabled={saving || (editing.bookedRooms || 0) > 0}
              title={(editing.bookedRooms || 0) > 0 ? "This date has booked rooms and cannot be deleted" : ""}
              onClick={() => remove(editing, true)}
            >
              Delete record
            </button>
          )}
          <button className="bmt-secondary-btn" disabled={saving} onClick={closeForm}>Cancel</button>
          <button className="bmt-primary-btn" disabled={saving || formCounts === null} onClick={save}>
            {saving ? "Saving..." : editing ? "Save changes" : targetDates.length > 1 ? `Save ${targetDates.length} dates` : "Save availability"}
          </button>
        </div>
      </div>
    );
  }

  /* ==========================================================
     LIST VIEW
  ========================================================== */
  const cell = { padding: "10px", borderBottom: "1px solid #f3f4f6" };
  const modeBtn = (id, text) => (
    <button type="button" className={displayMode === id ? "bmt-primary-btn" : "bmt-secondary-btn"} onClick={() => setDisplayMode(id)}>{text}</button>
  );

  return (
    <div className="bmt-premium-page">
      {header}

      <Notice kind="error">{error}</Notice>
      <Notice kind="success">{success}</Notice>

      <section className="bmt-form-card">
        <div className="bmt-form-grid">
          <div className="bmt-field-group">
            <label>Resort</label>
            <select value={resortId} onChange={(e) => { setResortId(e.target.value); setError(""); setSuccess(""); }} disabled={loadingResorts}>
              {resorts.map((r) => <option key={r._id} value={r._id}>{r.name}</option>)}
            </select>
          </div>
          <div className="bmt-field-group">
            <label>Room category</label>
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setError(""); setSuccess(""); }}>
              <option value="ALL">All categories</option>
              {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button className="bmt-secondary-btn" onClick={() => shiftMonth(-1)} aria-label="Previous month">‹ Prev</button>
            <strong style={{ minWidth: 150, textAlign: "center" }}>{MONTHS[month]} {year}{loadingRecords ? " (loading...)" : ""}</strong>
            <button className="bmt-secondary-btn" onClick={() => shiftMonth(1)} aria-label="Next month">Next ›</button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {modeBtn("list", "List")}
            {modeBtn("calendar", "Calendar")}
          </div>
        </div>

        {displayMode === "list" && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <button
              type="button"
              onClick={() => setStatusFilter("ALL")}
              style={{
                padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer",
                border: `1px solid ${statusFilter === "ALL" ? "#111827" : "#e5e7eb"}`,
                background: statusFilter === "ALL" ? "#111827" : "#fff",
                color: statusFilter === "ALL" ? "#fff" : "#374151",
              }}
            >
              All ({records.length})
            </button>
            {Object.entries(STATUS_STYLE).map(([k, s]) => (
              <button
                key={k}
                type="button"
                onClick={() => setStatusFilter(statusFilter === k ? "ALL" : k)}
                style={{
                  padding: "6px 12px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${statusFilter === k ? s.color : "#e5e7eb"}`,
                  background: statusFilter === k ? s.bg : "#fff",
                  color: s.color,
                }}
              >
                {s.label} ({counts[k]})
              </button>
            ))}
          </div>
        )}
      </section>

      {categories.length === 0 && (
        <section className="bmt-form-card">
          <div className="bmt-empty-state">
            <h3>No room categories in {resortName || "this resort"}</h3>
            <p>Create a room category from "Rooms & Categories" first, then add room units and set availability.</p>
          </div>
        </section>
      )}

      {/* ---------------- LIST ---------------- */}
      {categories.length > 0 && displayMode === "list" && (
        <section className="bmt-form-card">
          {loadingRecords ? (
            <p>Loading availability...</p>
          ) : records.length === 0 ? (
            <div className="bmt-empty-state">
              <h3>No availability records in {MONTHS[month]} {year}</h3>
              <p>Dates without a record have all rooms free. Add a record to block rooms, mark maintenance or close dates.</p>
              <button className="bmt-primary-btn" onClick={() => openAdd()}>Add availability</button>
            </div>
          ) : shown.length === 0 ? (
            <p>No records match this status filter.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900, fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#6b7280", fontSize: 13 }}>
                    {["Date", "Category", "Total", "Booked", "Blocked", "Maint.", "Available", "Status", "Notes", ""].map((h) => (
                      <th key={h} style={{ padding: "8px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const open = expandedId === r._id;
                    const units = Array.isArray(r.roomUnits) ? r.roomUnits : [];
                    const hasBooked = (r.bookedRooms || 0) > 0;
                    return (
                      <Fragment key={r._id}>
                        <tr>
                          <td style={cell}><strong>{fmtDate(r.date)}</strong></td>
                          <td style={cell}>{catNameOf(r)}</td>
                          <td style={cell}>{r.totalRooms}</td>
                          <td style={cell}>{r.bookedRooms}</td>
                          <td style={cell}>{r.blockedRooms}</td>
                          <td style={cell}>{r.maintenanceRooms}</td>
                          <td style={cell}><strong>{r.availableRooms}</strong></td>
                          <td style={cell}><StatusBadge status={r.status} /></td>
                          <td style={{ ...cell, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#6b7280" }} title={r.notes}>{r.notes || "—"}</td>
                          <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                            <button className="bmt-secondary-btn" style={{ marginRight: 6 }} onClick={() => setExpandedId(open ? "" : r._id)} aria-expanded={open}>
                              Rooms
                            </button>
                            <button className="bmt-secondary-btn" style={{ marginRight: 6 }} onClick={() => openEdit(r)}>Edit</button>
                            <button
                              className="bmt-secondary-btn"
                              onClick={() => remove(r)}
                              disabled={hasBooked}
                              title={hasBooked ? "This date has booked rooms and cannot be deleted" : "Delete record"}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={10} style={{ ...cell, background: "#f9fafb" }}>
                              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
                                Rooms currently marked Available in Room Units ({r.currentlyAvailableUnits ?? units.length}). This list is not date-specific.
                              </div>
                              {units.length > 0 ? (
                                <div className="bmt-chip-wrap">
                                  {units.map((u) => (
                                    <span className="bmt-chip" key={u._id} title={[u.building, u.floor].filter(Boolean).join(", ")}>
                                      {u.roomNumber}{u.floor ? ` • ${u.floor}` : ""}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ fontSize: 13 }}>
                                  {r.status === "AVAILABLE" && r.availableRooms > 0 ? "No rooms are marked Available right now." : "No rooms can be booked on this date."}
                                </span>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ---------------- CALENDAR ---------------- */}
      {categories.length > 0 && displayMode === "calendar" && (
        <section className="bmt-form-card">
          {categoryFilter === "ALL" ? (
            <div className="bmt-empty-state">
              <h3>Choose a room category</h3>
              <p>The calendar shows one room category at a time. Select a category above to see its free rooms for each date.</p>
            </div>
          ) : calTotal === 0 ? (
            <div className="bmt-empty-state">
              <h3>No active room units in this category</h3>
              <p>Add room units from "Room Units" first, then availability will show here.</p>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4 }}>
                {WEEKDAYS.map((d) => (
                  <div key={d} style={{ textAlign: "center", fontSize: 12, fontWeight: 600, color: "#6b7280", padding: 4 }}>{d}</div>
                ))}
                {calendarCells.map((c, i) => {
                  if (!c) return <div key={`e${i}`} />;
                  const rec = recordByDate[c.date];
                  const st = rec ? STATUS_STYLE[rec.status] || STATUS_STYLE.AVAILABLE : null;
                  const available = rec ? rec.availableRooms : calTotal;
                  const totalRoomsShown = rec ? rec.totalRooms : calTotal;
                  return (
                    <button
                      key={c.date}
                      type="button"
                      onClick={() => (rec ? openEdit(rec) : openAdd(c.date))}
                      aria-label={`${c.date}, ${rec ? `${st.label}, ${available} of ${totalRoomsShown} rooms free` : `${calTotal} rooms free, no changes set`}`}
                      style={{
                        minHeight: 64, padding: 6, borderRadius: 8, cursor: "pointer", textAlign: "left",
                        display: "flex", flexDirection: "column", justifyContent: "space-between",
                        border: `1px solid ${c.date === todayYmd() ? "#2563eb" : "#e5e7eb"}`,
                        background: st ? st.bg : "#fff",
                        fontFamily: "inherit",
                      }}
                    >
                      <span style={{ fontSize: 12, color: "#6b7280" }}>{c.day}</span>
                      <span style={{ fontSize: 12, fontWeight: rec ? 700 : 400, color: st ? st.color : "#9ca3af" }}>
                        {rec && (rec.status === "CLOSED" || rec.status === "BLOCKED" || rec.status === "SOLD_OUT") ? st.label : `${available}/${totalRoomsShown} free`}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 12, fontSize: 12, color: "#6b7280" }}>
                {Object.entries(STATUS_STYLE).map(([k, s]) => (
                  <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 3, background: s.bg, border: `1px solid ${s.color}` }} />
                    {s.label}
                  </span>
                ))}
                <span>Grey text = no record, all rooms free.</span>
              </div>
              <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>Click a date to add or edit its availability.</p>
            </>
          )}
        </section>
      )}
    </div>
  );
}